import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/layout/Layout';
import { SectionRenderer } from '../components/website/SectionRenderer';
import { SeoHead } from '../components/website/SeoHead';
import { brandName } from '../hooks/useCompanyBrand';

const NOINDEX_404 = { title: `Page Not Found | ${brandName()}`, no_index: true, no_follow: true };

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Universal CMS page renderer: resolves any published page by its URL path, honoring redirects.
// `draft` (admin-only, via ?cms=draft) renders the unpublished draft inside the real site layout for visual audits.
const DynamicPage = ({ draft: draftProp = false }) => {
  const { pathname, search } = useLocation();
  const draft = draftProp || new URLSearchParams(search).get('cms') === 'draft'; // custom pages: admins can preview drafts in place
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    const req = draft
      ? axios.get(`${API_URL}/admin/website/preview-by-path`, { params: { path }, headers: { Authorization: `Bearer ${localStorage.getItem('apollo_token')}` } })
      : axios.get(`${API_URL}/website/page`, { params: { path } });
    req
      .then((res) => { if (!cancelled) { setPage(res.data); setStatus('ok'); window.scrollTo(0, 0); } })
      .catch(async () => {
        try {
          const r = await axios.get(`${API_URL}/website/redirect`, { params: { path } });
          if (r.data.redirect && !cancelled) { navigate(r.data.redirect, { replace: true }); return; }
        } catch (_) { /* no redirect */ }
        if (!cancelled) setStatus('notfound');
      });
    return () => { cancelled = true; };
  }, [pathname, navigate, draft]);

  if (status === 'loading') {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center" data-testid="dynamic-page-loading">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (status === 'notfound' || !page) {
    return (
      <Layout>
        <SeoHead seo={NOINDEX_404} />
        <div className="min-h-screen flex flex-col items-center justify-center text-center px-4" data-testid="dynamic-page-notfound">
          <span className="label-overline">404</span>
          <h1 className="mt-4 text-neutral-900">Page not found</h1>
          <p className="mt-4 text-neutral-600">The page you're looking for doesn't exist or has moved.</p>
          <Link to="/" className="mt-8 btn-primary">Back to Home</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SeoHead seo={page.seo} path={page.path} fallbackTitle={`${page.name} | ${brandName()}`} />
      <div data-testid="dynamic-page" data-template={page.template} className="cms-root">
        {page.sections?.length > 0 ? <SectionRenderer sections={page.sections} /> : (
          <section className="pt-32 pb-20 bg-neutral-50"><div className="container-xl text-center"><h1 className="text-neutral-900">{page.name}</h1><p className="mt-4 text-neutral-600">This page has no content yet.</p></div></section>
        )}
      </div>
    </Layout>
  );
};

export default DynamicPage;
